import { useCallback, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { showSuccessToast } from "@stadium/services/react-toastify";
import { useConfig } from "@stadium/hooks/lib/use-config";
import { useLazyQuery } from "@stadium/services/apollo-client";
import keyBy from "lodash/keyBy";

import { batchPopulateOrder, populateOrder } from "@actions/order";

import { isGlobalCountry } from "@stadium/helpers/country/utils";
import {
  getCurrentQtyOfProductInCart,
  getProductType,
  computeExternalSourceIds,
} from "@helpers/orders/cart";
import { getCartOrderId, getCartOrderCurrency } from "@store/selectors/cart";
import { getStoreCurrencyCode, getStoreNumber } from "@store/selectors/store";
import { getCountry } from "@store/selectors/country";

import { convertPrice } from "@helpers/currency";
import { getConversionRates } from "@stadium/redux-utils/selectors/configSelector";

import useFindOrCreateDraftOrder from "@hooks/cart/use-find-or-create-draft-order";
import { fetchVariantsListForLockerProductCheck } from "@graphql/queries/stores/products/fetch-variant-list";

import { updateCart } from "@slices/cart";

import {
  ITEM_PRODUCT_TYPES,
  itemRemovedSuccessMessage,
  itemUpdatedSuccessMessage,
} from "@constants/orders/cart";
import { ITEM_UPDATE_SUCCESS_TOAST_ID, ITEM_REMOVE_SUCCESS_TOAST_ID } from "@constants/toast-ids";
import { byo, ON_DEMAND_PRODUCT, LOCKER_PRODUCT } from "@constants/products/detail";

const getDefaultVariables = (intent, currencyCode) => {
  return {
    product_type: getProductType(intent),
    currency_code: currencyCode,
  };
};

const usePopulateItems = () => {
  const currencyRef = useRef(null);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const { findOrCreateDraftOrder } = useFindOrCreateDraftOrder();
  const orderId = useSelector(getCartOrderId);
  const conversionRates = useConfig(getConversionRates);
  const orderCurrencyCode = useSelector(getCartOrderCurrency);
  const storeCurrencyCode = useSelector(getStoreCurrencyCode);
  const storeNumber = useSelector(getStoreNumber);
  const country = useSelector(getCountry);
  const globalZone = isGlobalCountry(country);
  const [fetchVariantsInfo] = useLazyQuery(fetchVariantsListForLockerProductCheck);

  currencyRef.current = orderCurrencyCode || storeCurrencyCode;

  const getPrice = ({ price, storePrice }) => {
    return storePrice
      ? convertPrice({
          value: storePrice,
          from: storeCurrencyCode,
          to: currencyRef.current,
          conversionRates,
        })
      : price;
  };

  const getPriceForGuestOrderSyncing = ({ price, orderCurrency, localOrderCurrency }) => {
    return orderCurrency !== localOrderCurrency
      ? convertPrice({
          value: price,
          from: localOrderCurrency,
          to: orderCurrency,
          conversionRates,
        })
      : price;
  };

  const postPopulateOrder = useCallback(
    (order) => {
      dispatch(updateCart(order));
      setLoading(false);

      return order;
    },
    [dispatch, setLoading],
  );

  const sendPopulateCartRequest = useCallback(
    (orderId, payload, isBatchPopulate = false) => {
      setLoading(true);
      return dispatch(
        isBatchPopulate ? batchPopulateOrder(orderId, payload) : populateOrder(orderId, payload),
      )
        .then(postPopulateOrder)
        .catch((err) => {
          setLoading(false);
          throw err;
        });
    },
    [dispatch, postPopulateOrder],
  );

  const getUpdatedPayload = (payload, intent) => {
    return {
      ...payload,
      ...getDefaultVariables(intent, currencyRef.current),
    };
  };

  const sendRequest = useCallback(
    ({ getPayload, intent, isBatchPopulate }) => {
      if (orderId) {
        const payload = getPayload();
        return sendPopulateCartRequest(
          orderId,
          getUpdatedPayload(payload, intent),
          isBatchPopulate,
        );
      } else {
        return findOrCreateDraftOrder().then((order) => {
          currencyRef.current = order.currency;
          const payload = getPayload();
          sendPopulateCartRequest(order.id, getUpdatedPayload(payload, intent), isBatchPopulate);
        });
      }
    },
    [orderId, findOrCreateDraftOrder, sendPopulateCartRequest],
  );

  const addByoItemInCart = useCallback(
    ({ medleyItemAttributes, medleyId, zoneId = "", itemType = ON_DEMAND_PRODUCT }) => {
      const getPayload = () => ({
        medley_item_set_id: null,
        medley_id: medleyId,
        medley_items_attributes: medleyItemAttributes.map((attributes) => ({
          ...attributes,
          price: getPrice({ price: attributes.price, storePrice: attributes.storePrice }),
        })),
        mark_for_destroy: false,
        quantity: 1,
        zone_id: zoneId,
        item_type: itemType,
      });
      return sendRequest({ getPayload, intent: byo });
    },
    [sendRequest],
  );

  const updateByoComponentsInCart = useCallback(
    ({
      medleyItemAttributes,
      medleyId,
      medleyItemSetId,
      zoneId = "",
      itemType = ON_DEMAND_PRODUCT,
    }) => {
      const getPayload = () => ({
        medley_item_set_id: medleyItemSetId,
        medley_id: medleyId,
        medley_items_attributes: medleyItemAttributes.map((attributes) => ({
          ...attributes,
          price: getPrice({ price: attributes.price, storePrice: attributes.storePrice }),
        })),
        mark_for_destroy: false,
        zone_id: zoneId,
        item_type: itemType,
      });
      return sendRequest({ getPayload, intent: byo });
    },
    [sendRequest],
  );
  const deleteByoItemFromCart = useCallback(
    ({ medleyItemSetId, intent, zoneId = "" }) => {
      const getPayload = () => ({
        medley_item_set_id: medleyItemSetId,
        mark_for_destroy: true,
        zone_id: zoneId,
      });
      return sendRequest({ getPayload, intent }).then(() =>
        showSuccessToast({
          message: itemRemovedSuccessMessage,
          config: { toastId: ITEM_REMOVE_SUCCESS_TOAST_ID },
        }),
      );
    },
    [sendRequest],
  );

  const updateByoItemFromCart = useCallback(
    ({ medleyItemSetId, quantity, intent, zoneId }) => {
      const getPayload = () => ({
        medley_item_set_id: medleyItemSetId,
        quantity,
        mark_for_destroy: false,
        zone_id: zoneId || "",
      });
      return sendRequest({ getPayload, intent }).then(() =>
        showSuccessToast({
          message: itemUpdatedSuccessMessage,
          config: { toastId: ITEM_UPDATE_SUCCESS_TOAST_ID },
        }),
      );
    },
    [sendRequest],
  );

  const syncOrders = useCallback(
    async (localOrder, order) => {
      const { lineItems, productSets } = localOrder.lineItems.reduce(
        (data, item) => {
          const { productSets, lineItems } = data;
          item.isCollectionType ? productSets.push(item) : lineItems.push(item);
          return { lineItems, productSets };
        },
        { productSets: [], lineItems: [] },
      );

      const { externalSourceIds, catalogExternalStoreProductMappings } = computeExternalSourceIds({
        lineItems,
        productSets,
      });
      const { data } = await fetchVariantsInfo({
        variables: {
          globalZone,
          country: globalZone ? "" : country.iso,
          storeNumber,
          externalSourceIds,
          catalogExternalStoreProductMappings,
        },
      });
      const variantsMapping = keyBy(data.variants, "externalId");
      localOrder.lineItems = localOrder.lineItems.map((item) => {
        return {
          ...item,
          itemType: variantsMapping[item.externalId]?.catalogVariants.find(
            (variant) => variant.catalogStoreExternalId === item.storeId,
          )?.lockerProduct
            ? LOCKER_PRODUCT
            : ON_DEMAND_PRODUCT,
        };
      });

      syncOrdersAfterLockerPriceValidate(localOrder, order);
    },
    [fetchVariantsInfo, country.iso, storeNumber, globalZone],
  );

  const syncOrdersAfterLockerPriceValidate = useCallback(
    (localOrder, order) => {
      const lineItemAttributes = [];
      const productSetAttributes = [];
      localOrder.lineItems.forEach((item) => {
        const commonAtrributes = {
          store_id: item?.storeId,
          restrictions: item?.restrictionsForPayload || "",
          dietary_restrictions: item?.dietaryRestrictionForPayload,
          dietary_preference: item?.dietaryPreference,
          zone_id: item?.zoneId || "",
          item_type: item?.itemType || ON_DEMAND_PRODUCT,
        };
        if (item.isCollectionType) {
          const [product_id, price] = item.productId.split("-");
          productSetAttributes.push({
            product_id,
            price: getPriceForGuestOrderSyncing({
              price: +price,
              orderCurrency: order.currency,
              localOrderCurrency: item.localCartCurrency,
            }),
            quantity: item.quantity,
            product_type: ITEM_PRODUCT_TYPES.collection,
            ...commonAtrributes,
          });
        } else {
          lineItemAttributes.push({
            product_id: item.productId,
            price: getPriceForGuestOrderSyncing({
              price: item.price,
              orderCurrency: order.currency,
              localOrderCurrency: item.localCartCurrency,
            }),
            vendor_id: item.vendorId,
            quantity:
              item.quantity +
              (order.id
                ? getCurrentQtyOfProductInCart({
                    order,
                    productId: item.productId,
                    currentCatalogExternalStoreId: item?.storeId,
                  })
                : 0),
            product_type: ITEM_PRODUCT_TYPES.product,
            ...commonAtrributes,
          });
        }
      });
      const medley_item_set_attributes = localOrder.medleyItemSets.map((item) => ({
        medley_item_set_id: null,
        medley_id: item.medleyId,
        medley_items_attributes: item.medleyComponents.map((component) => ({
          product_id: component.productId,
          price: getPriceForGuestOrderSyncing({
            price: component.price,
            orderCurrency: order.currency,
            localOrderCurrency: component.localCartCurrency,
          }),
          quantity: component.quantity,
          images: component.images,
          name: component.name,
          slug: component.slug,
          zone_id: item?.zoneId || "",
          item_type: item?.itemType || ON_DEMAND_PRODUCT,
        })),
        mark_for_destroy: false,
        quantity: item.quantity,
        zone_id: item?.zoneId || "",
        item_type: item?.itemType || ON_DEMAND_PRODUCT,
      }));
      const payload = {
        currency_code: order.currency,
        line_items_attributes: lineItemAttributes,
        product_sets_attributes: productSetAttributes,
        medley_item_set_attributes,
      };
      return sendPopulateCartRequest(order.id, payload, true);
    },
    [sendRequest],
  );

  const addMultipleItemsInCart = useCallback(
    (products, intent) => {
      const getPayload = () => {
        const lineItemAttributes = [];
        const productSetAttributes = [];
        products.forEach((item) => {
          const commonAtrributes = {
            product_id: item.productId,
            price: getPrice({ price: item.price, storePrice: item.storePrice }),
            quantity: item.quantity,
            store_id: item?.storeId,
            restrictions: item?.restrictionsForPayload || "",
            dietary_restrictions: item?.dietaryRestrictionForPayload,
            dietary_preference: item?.dietaryPreference,
            item_type: item?.itemType || ON_DEMAND_PRODUCT,
            zone_id: item?.zoneId || "",
          };
          if (item.isCollectionType) {
            productSetAttributes.push({
              product_type: ITEM_PRODUCT_TYPES.collection,
              ...commonAtrributes,
            });
          } else {
            lineItemAttributes.push({
              product_type: ITEM_PRODUCT_TYPES.product,
              vendor_id: item.vendorId,
              ...commonAtrributes,
            });
          }
        });
        return {
          line_items_attributes: lineItemAttributes,
          product_sets_attributes: productSetAttributes,
        };
      };

      return sendRequest({ getPayload, intent, isBatchPopulate: true });
    },
    [sendRequest],
  );

  const addItemInCart = useCallback(
    ({
      productId,
      quantity = 1,
      price,
      storePrice,
      intent,
      storeId,
      vendorId,
      dietaryPreference = "",
      zoneId = "",
      itemType = ON_DEMAND_PRODUCT,
    }) => {
      const getPayload = () => ({
        product_id: productId,
        price: getPrice({ price, storePrice }),
        quantity,
        store_id: storeId,
        vendor_id: vendorId,
        dietary_preference: dietaryPreference,
        item_type: itemType,
        zone_id: zoneId,
      });
      return sendRequest({ getPayload, intent });
    },
    [sendRequest],
  );

  const deleteItemFromCart = useCallback(
    ({ productId, intent, storeId, zoneId = "" }) => {
      const getPayload = () => ({
        product_id: productId,
        quantity: 0,
        store_id: storeId,
        zone_id: zoneId,
      });
      return sendRequest({ getPayload, intent }).then(() =>
        showSuccessToast({
          message: itemRemovedSuccessMessage,
          config: { toastId: ITEM_REMOVE_SUCCESS_TOAST_ID },
        }),
      );
    },
    [sendRequest],
  );

  const updateItemInCart = useCallback(
    ({
      productId,
      quantity,
      price,
      storePrice,
      intent,
      storeId,
      dietaryPreference = "",
      zoneId = "",
      vendorId,
    }) => {
      const getPayload = () => ({
        product_id: productId,
        price: getPrice({ price, storePrice }),
        quantity,
        store_id: storeId,
        dietary_preference: dietaryPreference,
        zone_id: zoneId,
        vendor_id: vendorId,
      });
      return sendRequest({ getPayload, intent }).then(() =>
        showSuccessToast({
          message: itemUpdatedSuccessMessage,
          config: { toastId: ITEM_UPDATE_SUCCESS_TOAST_ID },
        }),
      );
    },
    [sendRequest],
  );

  const addCollectionItemInCart = useCallback(
    ({
      productId,
      quantity = 1,
      price,
      storePrice,
      intent,
      restrictions = "",
      dietaryRestrictions = {},
      dietaryPreference = "",
      zoneId = "",
      itemType = ON_DEMAND_PRODUCT,
      isRestrictionsSelected = false,
    }) => {
      const getPayload = () => ({
        product_id: productId,
        price: getPrice({ price, storePrice }),
        quantity,
        restrictions,
        dietary_restrictions: dietaryRestrictions,
        dietary_preference: dietaryPreference,
        zone_id: zoneId,
        item_type: itemType,
        is_restrictions_selected: isRestrictionsSelected,
      });
      return sendRequest({ getPayload, intent });
    },
    [sendRequest],
  );

  const deleteCollectionItemFromCart = useCallback(
    ({ orderProductSetId, intent, zoneId = "" }) => {
      const getPayload = () => ({
        order_product_set_id: orderProductSetId,
        quantity: 0,
        mark_for_destroy: true,
        zone_id: zoneId,
      });
      return sendRequest({ getPayload, intent }).then(() =>
        showSuccessToast({
          message: itemRemovedSuccessMessage,
          config: { toastId: ITEM_REMOVE_SUCCESS_TOAST_ID },
        }),
      );
    },
    [sendRequest],
  );

  const updateCollectionItemInCart = useCallback(
    ({
      orderProductSetId,
      quantity,
      price,
      storePrice,
      orderProductSetRestrictionId,
      intent,
      dietaryPreference = "",
      restrictions,
      dietaryRestrictions,
      showToast = true,
      zoneId = "",
      itemType = ON_DEMAND_PRODUCT,
      isRestrictionsSelected,
    }) => {
      const getPayload = () => ({
        order_product_set_id: orderProductSetId,
        price: getPrice({ price, storePrice }),
        quantity,
        order_product_set_restriction_id: orderProductSetRestrictionId,
        dietary_preference: dietaryPreference,
        restrictions,
        dietary_restrictions: dietaryRestrictions,
        zone_id: zoneId,
        item_type: itemType,
        is_restrictions_selected: isRestrictionsSelected,
      });
      return sendRequest({ getPayload, intent }).then(
        () =>
          showToast &&
          showSuccessToast({
            message: itemUpdatedSuccessMessage,
            config: { toastId: ITEM_UPDATE_SUCCESS_TOAST_ID },
          }),
      );
    },
    [sendRequest],
  );

  const updateMultipleItemsInCart = useCallback(
    ({ lineItems = [], productSets = [], medleySets = [] }, orderId) => {
      const lineItemAttributes = lineItems.map((item) => ({
        product_id: item.productId,
        price: item.price,
        vendor_id: item.vendorId,
        quantity: item.quantity,
        product_type: ITEM_PRODUCT_TYPES.product,
        store_id: item?.storeId,
        restrictions: item?.restrictionsForPayload || "",
        dietary_restrictions: item?.dietaryRestrictionForPayload,
        dietary_preference: item?.dietaryPreference,
        zone_id: item?.zoneId || "",
        item_type: item?.itemType || ON_DEMAND_PRODUCT,
      }));
      const productSetAttributes = productSets.map((item) => ({
        product_id: item.productId,
        price: item.price,
        vendor_id: item.vendorId,
        quantity: item.quantity,
        product_type: ITEM_PRODUCT_TYPES.collection,
        store_id: item?.storeId,
        restrictions: item?.restrictionsForPayload || "",
        dietary_restrictions: item?.dietaryRestrictionForPayload,
        dietary_preference: item?.dietaryPreference,
        zone_id: item?.zoneId || "",
        item_type: item?.itemType || ON_DEMAND_PRODUCT,
      }));

      const medley_item_set_attributes = medleySets.map((item) => ({
        medley_item_set_id: item.id,
        medley_id: item.medleyId,
        medley_items_attributes: item.medleyComponents.map((component) => ({
          product_id: component.productId,
          price: getPrice({ price: component.price, storePrice: component.storePrice }),
          quantity: component.quantity,
          images: component.images,
          name: component.name,
          slug: component.slug,
        })),
        mark_for_destroy: false,
        quantity: item.quantity,
      }));
      const payload = {
        currency_code: currencyRef.current,
        line_items_attributes: lineItemAttributes,
        product_sets_attributes: productSetAttributes,
        medley_item_set_attributes,
      };
      return sendPopulateCartRequest(orderId, payload, true);
    },
    [sendPopulateCartRequest, getPrice],
  );

  return {
    loading,
    addCollectionItemInCart,
    addItemInCart,
    syncOrders,
    addMultipleItemsInCart,
    updateMultipleItemsInCart,
    deleteCollectionItemFromCart,
    deleteItemFromCart,
    updateCollectionItemInCart,
    updateItemInCart,
    addByoItemInCart,
    updateByoComponentsInCart,
    updateByoItemFromCart,
    deleteByoItemFromCart,
  };
};

export default usePopulateItems;
